import React from 'react';
import PropTypes from 'prop-types';

import {AppFlags} from '../../../app/app-types';
import {connectAppEnv} from '../../../app/app-env';
import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEIcon from '../../elements/oe-icon';
import OEButton, {OEIconButton} from '../../elements/controls/oe-button';
import {OEColorPickerNumericInputType, OEColorPickerButton} from '../../color-picker/oe-color-picker';
import {OEGroupControl, OESliderControl} from '../../elements/controls/oe-controls';
import OEIconCodes from '../../../lib/oe-icon-codes';
import OEPopover from '../../views/oe-popover';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import {oeUniqueIdGen} from '../../../lib/tools/oe-unique-id-gen';
import retardUpdate from '../../../lib/react/update-retarder';
import {OEArrowChangeFlags} from './oe-arrow-model';
import {OEArrowExtPopoverController} from './oe-arrow-ext-controller';

export class OEArrowPopoverController extends React.PureComponent {

    constructor(props)  {
        super(props);

        this.uid = oeUniqueIdGen.get();
        this.popoverTargetId = 'popover-target-id-' + this.uid;

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = null;
        this.isOpen = false;
        this.position = null;

        this.state = this.defaultState();
        
        // since there is no reason to include constant values in this.state we just define them in class scope
        this.minWidthBound = 0.15;
        this.maxWidthBound = 0.75*3.5;
        this.minLengthBound = 0.3;
        this.maxLengthBound = 3.5;

        this.tipStyleImages = [
            "../images/components/arrow/arrowTip_1x.png",
            "../images/components/arrow/arrowTail_1x.png",
            "../images/components/arrow/arrowRound_1x.png",
            "../images/components/arrow/arrowFlat_1x.png"
        ];

        this.endStyleImages = [
            "../images/components/arrow/arrowTip_1x2.png",
            "../images/components/arrow/arrowTail_1x2.png",
            "../images/components/arrow/arrowRound_1x2.png",
            "../images/components/arrow/arrowFlat_1x2.png"
        ];
    
        this.onWindowResized = this.onWindowResized.bind(this);
        
        this.updateIsOpenState = this.updateIsOpenState.bind(this);
        this.onArrowLongTouched = this.onArrowLongTouched.bind(this);
        this.onArrowRemoved = this.onArrowRemoved.bind(this);
        this.onArrowChanged = this.onArrowChanged.bind(this);
        this.onUIControllerStateChanged = this.onUIControllerStateChanged.bind(this);

        // definition of callbacks used in the render method 
        /* Since inline definitions for function properties, e.g., definitions of the kind property={() => this.callback()}, 
           are providing a new function object to the child component every time the render method is called, 
           which leads to unnecessary updates of the child component,
           we consider such inline definitions as bad practise and define the callbacks in class scope.
           Note that this is especially critical for callbacks for the ref property, because every time a new function is set to ref react calls the function immediately, even if the ref is not changed!
           This can even cause feedback loops if obtaining a reference leads to state updates!!
        */
        this.onArrowExtControllerRef = this.onArrowExtControllerRef.bind(this);
        this.renderAdditionalButtons = this.renderAdditionalButtons.bind(this);

        this.onToggle = this.onToggle.bind(this);
        this.onExtBtnPressed = this.onExtBtnPressed.bind(this);
        this.onArrowExtPopoverToggle = this.onArrowExtPopoverToggle.bind(this);
        this.onStyleTransferToAllBtnPressed = this.onStyleTransferToAllBtnPressed.bind(this);
        this.onDumpBtnPressed = this.onDumpBtnPressed.bind(this);
        this.onTipChangeBtnPressed = this.onTipChangeBtnPressed.bind(this);
        this.onEndChangeBtnPressed = this.onEndChangeBtnPressed.bind(this);
        this.onArrowColorButtonChanged = this.onArrowColorButtonChanged.bind(this);
        this.onArrowLengthChanged = this.onArrowLengthChanged.bind(this);
        this.onArrowWidthChanged = this.onArrowWidthChanged.bind(this);
    }

    defaultState()  {
        return {
            id: this.id,
            isOpen: this.isOpen,
            position: this.position,
            defaultArrowColor: {x: 0, y: 0, z: 0},
            style: {
                tipStyle: null,
                endStyle: null,
                width: 0.5,
                length: 1,
                color: {x: 0, y: 0, z: 0},
                smooth: true
            },
            isArrowExtPopoverOpen: false
        };
    }
    
    componentDidMount()    {
        window.addEventListener('resize', this.onWindowResized);
    }

    componentWillUnmount()    {
        window.removeEventListener('resize', this.onWindowResized);
    }

    componentDidUpdate(prevProps, prevState)    {
        if(!OEToolbox.shallowEqual(this.state.position, prevState.position))    {
            if(this.arrowExtControllerRef)  this.arrowExtControllerRef.forceUpdate(); // needed for positioning update
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowLongTouched, this.onArrowLongTouched);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowsStateChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowRemoved, this.onArrowRemoved);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.arrowChanged, this.onArrowChanged);
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowLongTouched, this.onArrowLongTouched);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowsStateChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowEditModeChanged, this.updateIsOpenState);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowRemoved, this.onArrowRemoved);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.arrowChanged, this.onArrowChanged);
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.uiControllerStateChanged, this.onUIControllerStateChanged);	
    }

    onOpenStateChanged()    {
        if(!this.oe.isReady()) return;
        let controller = this.oe.sharedInterface.getUIControllerModelState();
        controller.setUpdateEnabled(!this.isOpen);
        
        let userInfo = new this.oe.Module.Dictionary();
        this.oe.sharedInterface.postNotification(this.oe.NotificationName.uiInstanceArrowPopoverOpenStateChanged, userInfo.setBoolValue('open', this.isOpen));
        userInfo.delete();
    }

    close() {
        if(!this.isOpen) return;
        this.isOpen = false;
        this.setState({
            isOpen: false,
            isArrowExtPopoverOpen: false
        });
        this.onOpenStateChanged();
    }

    open(pos)   {
        if(!this.canOpen()) return;

        if(this.isOpen)	{
            if(!pos || OEToolbox.shallowEqual(pos, this.position)) return;
            this.position = pos;
            this.setState({position: this.position});
            return;
        }

        this.isOpen = true;
        if(pos)	{
            this.position = pos;
            this.setState({isOpen: true, position: this.position});
        } else {
            this.setState({isOpen: true});
        }
        
        this.onOpenStateChanged();
    }
    
    canOpen()	{
        if(!this.oe.isReady()) return false;
        let controller = this.oe.sharedInterface.getUIControllerArrow();
        return controller.getUIEnabled() && controller.getEnabled() && controller.isEditingEnabled();
    }

    getPosition()   {
        return this.position;
    }

    setPosition(pos)    {
        if(OEToolbox.shallowEqual(pos, this.position)) return;
        this.position = pos;
        this.setState({position: this.position});
    }
    
    updatePos() {
        if(!this.oe.isReady() || this.id === null) return;    // it is not save to call UIControllerArrow methods with null for id

        let pos = this.oe.sharedInterface.getUIControllerArrow().getArrowScreenCenter(this.id);

        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        pos = {x: pos.x * devicePR, y: pos.y * devicePR};

        this.setPosition(pos);
    }
    
    onWindowResized()   {
        this.updatePos();

        // the arrow is not correct repositioned, since the core module viewport has not been resized when the window sends the resize event
        // quick workaround -> update the position at a later time when the viewport has been updated 
        window.requestAnimationFrame(function() {
            window.requestAnimationFrame(this.updatePos.bind(this));
        }.bind(this));
    }

    updateArrowState(defColor)  {
        if(!this.oe.isReady() || this.id === null)    return;

        let style = this.oe.sharedInterface.getUIControllerArrow().getArrowStyle(this.id);
        this.setState({style: style});
 
        if(defColor)    this.setState({defaultArrowColor: style.color});
    }

    updateIsOpenState() {
        if(!this.canOpen()) this.close();
    }

    updateState_(released)   {
        if(!this.oe.isReady() || released === true)   {
            // here we have lost the core interface, i.e., this.oe is released 
            // we set the component in a save state which should also reflect that user input is not possible 
            this.id = null;
            this.position = null;
            this.setState(this.defaultState());
            this.close();
            return;
        }
        
        this.updateIsOpenState();
        this.updateArrowState(true);
    }

    updateState(released)   {
        retardUpdate(this, () => {
            this.updateState_(released)
        });
    }

    updateId(newId) {
        this.id = newId;
        this.setState({id: newId});
    }

    onArrowLongTouched(message, userInfo)   {
        if(!this.canOpen()) return;

        this.updateId(userInfo.ID);
        
        let pos = {x: userInfo.screenPos.x, y: userInfo.screenPos.y};
        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        pos = {x: pos.x * devicePR, y: pos.y * devicePR};

        this.open(pos);

        this.updateArrowState(true);
    }
    
    onArrowRemoved(message, userInfo) {
        if(userInfo.ID !== this.id)  return;
        this.close();
    }

    onArrowChanged(message, userInfo)   {
        // gets called when arrow state is changed
        if(userInfo.ID !== this.id || !(userInfo.changes & OEArrowChangeFlags.style))  return;    // ignore notifications for other arrows and non style changes
        this.updateArrowState();
    }

    onUIControllerStateChanged(message, userInfo)    {
        if(userInfo.type === this.oe.Module.UIControllerType.arrow) {
            this.updateIsOpenState();
        }
    }

    getTipStyleImage()  {
        let value = this.state.style.tipStyle !== null ? this.state.style.tipStyle.value : 0;
        return this.tipStyleImages[value % 4];
    }

    getEndStyleImage()  {
        let value = this.state.style.endStyle !== null ? this.state.style.endStyle.value : 2;
        return this.endStyleImages[value % 4];
    }

    onArrowExtControllerRef(ref)    {
        this.arrowExtControllerRef = ref;
    }

    renderAdditionalButtons(props)  {
        return (
            <React.Fragment>
                {!props.devFeatures ? null :
                    <OEIconButton className={props.buttonClassName + ' extended-btn'} activated={props.isArrowExtPopoverOpen} icon={OEIconCodes.arrowExt} onPressed={this.onExtBtnPressed}/>
                }
                <OEIconButton className={props.buttonClassName + ' style-transfer-to-all-btn'} iconClassName="rotate-180" icon={OEIconCodes.arrowStyleTransferToAll} onPressed={this.onStyleTransferToAllBtnPressed}/>
                <OEIconButton className={props.buttonClassName + ' dump-btn'} icon={OEIconCodes.arrowDump} onPressed={this.onDumpBtnPressed}/>
            </React.Fragment>
        );
    }
    
    render()    {
        const position = !this.state.position ? null : {x: this.state.position.x + this.props.offset.x, y: this.state.position.y + this.props.offset.y};

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>
                <OEPopover
                    moduleId={this.props.moduleId}
                    className="popover-control"
                    boundariesElement={this.props.boundariesElement}
                    position={position}
                    buttonClassName="transparent-btn"
                    headerSeparator={false}
                    isOpen={this.state.isOpen && this.state.position !== null}
                    additionalButtons={this.renderAdditionalButtons}
                    backdrop={true}
                    backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                    onToggle={this.onToggle}
                    popoverTargetId={this.popoverTargetId}
                    devFeatures={this.props.devFeatures}
                    isArrowExtPopoverOpen={this.state.isArrowExtPopoverOpen}
                >

                    <div className="arrow-popover">
                        <div className="arrow-tool-padding">
                            <OEColorPickerButton
                                moduleId={this.props.moduleId}
                                boundariesElement={this.props.boundariesElement}
                                noHeader={true}
                                backdrop={true}
                                backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                                color={this.state.style.color}
                                defaultColor={this.state.defaultArrowColor}
                                hasAlphaControl={false}
                                numericInput={this.props.devFeatures ? OEColorPickerNumericInputType.textField : OEColorPickerNumericInputType.rgbInput}
                                hasPaletteButton={this.props.devFeatures}
                                onChange={this.onArrowColorButtonChanged}
                            />
                            <OEButton className="arrow-tool-tip" onPressed={this.onTipChangeBtnPressed}>
                                <img src={this.getTipStyleImage()}/>
                            </OEButton>
                            <OEButton className="arrow-tool-middle p-0">
                                <img src="../images/components/arrow/arrowMiddle_1x.png"/>
                            </OEButton>
                            <OEButton className="arrow-tool-end p-0" onPressed={this.onEndChangeBtnPressed}>
                                <img src={this.getEndStyleImage()}/>
                            </OEButton>
                        </div>
                        <OEGroupControl>
                            <OESliderControl
                                title={<OEIcon code={OEIconCodes.arrowLength}/>}
                                className="popover-slider-setting"
                                min={this.minLengthBound}
                                max={this.maxLengthBound}
                                step={0.001}
                                value={this.state.style.length}
                                onSlide={this.onArrowLengthChanged}
                            />
                            <OESliderControl
                                title={<OEIcon code={OEIconCodes.arrowWidth}/>}
                                className="popover-slider-setting"
                                min={this.minWidthBound}
                                max={this.maxWidthBound}
                                step={0.001}
                                value={this.state.style.width}
                                onSlide={this.onArrowWidthChanged}
                            />
                        </OEGroupControl>
                    </div>

                    {!this.props.devFeatures ? null :
                        <OEArrowExtPopoverController
                            ref={this.onArrowExtControllerRef}
                            moduleId={this.props.moduleId}
                            className="with-backdrop"
                            appComponent={this.props.appComponent}
                            id={this.state.id}
                            boundariesElement={this.props.boundariesElement}
                            target={this.popoverTargetId}
                            placement="right-start"
                            hideArrow={true}
                            noHeader={true}
                            isOpen={this.state.isArrowExtPopoverOpen}
                            onToggle={this.onArrowExtPopoverToggle}
                            backdrop={false}
                            backdropOptions={{color: 'rgba(0, 0, 0, 0)'}}
                            devFeatures={this.props.devFeatures}
                        />
                    }
                </OEPopover>
            </React.Fragment>
        );
    }

    onToggle() {
        this.close();
    }

    onExtBtnPressed()   {
        let isArrowExtPopoverOpen = this.state.isArrowExtPopoverOpen;
        this.setState({isArrowExtPopoverOpen: !isArrowExtPopoverOpen});
    }

    onArrowExtPopoverToggle()   {
        this.setState({isArrowExtPopoverOpen: false});
    }

    onStyleTransferToAllBtnPressed()  {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerArrow().applyArrowStyleToAll(this.id);
    }
    
    onDumpBtnPressed()  {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerArrow().removeArrow(this.id);
    }
    
    onTipChangeBtnPressed() {
        if(!this.oe.isReady() || this.id === null) return;
        let style = this.oe.sharedInterface.getUIControllerArrow().getArrowTipStyle(this.id);
        this.oe.sharedInterface.getUIControllerArrow().setArrowTipStyle(this.id, {value: (style.value + 1) % 4});
    }

    onEndChangeBtnPressed() {
        if(!this.oe.isReady() || this.id === null) return;
        let style = this.oe.sharedInterface.getUIControllerArrow().getArrowEndStyle(this.id);
        this.oe.sharedInterface.getUIControllerArrow().setArrowEndStyle(this.id, {value: (style.value + 1) % 4});
    }

    onArrowColorButtonChanged(sender, color) {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerArrow().setArrowColor(this.id, color, false, 0.0);
    }

    onArrowLengthChanged(sender, value) {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerArrow().setArrowLength(this.id, value);
    }

    onArrowWidthChanged(sender, value) {
        if(!this.oe.isReady() || this.id === null) return;
        this.oe.sharedInterface.getUIControllerArrow().setArrowWidth(this.id, value);
    }
}

OEArrowPopoverController.defaultProps = {
    moduleId: '',
    devFeatures: false
};

OEArrowPopoverController.propTypes = {
    moduleId: PropTypes.string,
    devFeatures: PropTypes.bool
}; 

export default frefFromRef(connectAppEnv((env) => { 
    return {
        appComponent: env.component,
        devFeatures: env.config.module.flags && env.config.module.flags.includes(AppFlags.import)
    };
})(connectModuleEnv((env) => {
    return {
        offset: {x: 0, y: -env.ui.capBar.edgeOffsets.top}
    };
})(frefToRef(OEArrowPopoverController))));